import { RequestParams } from '../../api';
import { getCurrencyApi, getCurrencyTransfersApi } from '../../api/currency';
import { getAllTransactionApi, getBlockchainTransactionsList } from '../../api/transaction';
import { errorNotification } from '../../helpers/notification';
import { isCommonError, isDomExceptionlError } from '../../helpers/typeGuards';
import { AppState, GetState, ThunkAppDispatch } from '../../store';
import { getCurrencySelector } from '../currency/selector';
import { getCurrencyThunk } from '../currency/thunk';
import {
  getTransactionsListAction,
  setCurrencyInfoAction,
  setCurrencyTransfersListAction,
} from './action';
import {
  getCurrencyInfoSelector,
  getLastCurrencyTimeUpdate,
  getLastTransactionTimeUpdate,
} from './selector';

const isCurrentNewest = (
  getState: GetState,
  selector: (s: AppState) => number | null,
  time: number,
) => {
  const lastUpdateTime = selector(getState());

  return !lastUpdateTime || time > lastUpdateTime;
};

export const getTransactionsListThunk =
  (params: RequestParams, options: RequestInit = {}) =>
  (dispatch: ThunkAppDispatch, getState: GetState) => {
    const time = +new Date();
    if (params?.account) {
      return getBlockchainTransactionsList(params, options)
        .then((res) => {
          const isNewest = isCurrentNewest(getState, getLastTransactionTimeUpdate, time);
          if (isNewest) {
            dispatch(getTransactionsListAction(res, time));
          }
        })
        .catch((err) => {
          if (isDomExceptionlError(err) && err.code === err.ABORT_ERR) {
            return 'request cancel';
          }
          const isNewest = isCurrentNewest(getState, getLastTransactionTimeUpdate, time);
          if (isNewest) {
            errorNotification('Transaction table update has been failed');
          }
          return 1;
        });
    }

    return getAllTransactionApi(params, options)
      .then((res) => {
        const isNewest = isCurrentNewest(getState, getLastTransactionTimeUpdate, time);
        if (isNewest) {
          dispatch(getTransactionsListAction(res, time));
        }
      })
      .catch((err) => {
        if (isDomExceptionlError(err) && err.code === err.ABORT_ERR) {
          return 'request cancel';
        }
        const isNewest = isCurrentNewest(getState, getLastTransactionTimeUpdate, time);
        if (isNewest) {
          errorNotification('Transaction table update has been failed');
        }
      });
  };

export const getCurrencyInfoThunk = (code: string) => (dispatch: ThunkAppDispatch) => {
  return getCurrencyApi({ code })
    .then((res) => dispatch(setCurrencyInfoAction(res)))
    .catch((err) => {
      if (isCommonError(err) && err.cause.errorCode === 5) {
        errorNotification(`Currency not found`);
      } else {
        errorNotification('Currency information update has been failed');
      }
      dispatch(clearCurrencyInfoThunk());
      throw err;
    });
};

export const clearCurrencyInfoThunk = () => (dispatch: ThunkAppDispatch) => {
  dispatch(setCurrencyInfoAction(null));
};

export const getCurrencyTransactionsListThunk =
  ({ code, ...params }: RequestParams, options: RequestInit = {}) =>
  async (dispatch: ThunkAppDispatch, getState: GetState) => {
    const currency = getCurrencySelector(code as string)(getState());

    const time = +new Date();
    let currencyId: null | string = null;

    if (!currency) {
      const res = await getCurrencyApi({ code });
      currencyId = res.currency;
    }

    if (!currency && !currencyId) return Promise.resolve();

    return getCurrencyTransfersApi(
      {
        ...params,
        currency: currency?.currency ?? currencyId ?? '',
      },
      options,
    )
      .then((res) => {
        const isNewest = isCurrentNewest(getState, getLastCurrencyTimeUpdate, time);

        if (isNewest) {
          dispatch(setCurrencyTransfersListAction(res, time));
        }
      })
      .catch((err) => {
        if (isDomExceptionlError(err) && err.code === err.ABORT_ERR) {
          return 'cancel request';
        }
        const isNewest = isCurrentNewest(getState, getLastCurrencyTimeUpdate, time);
        if (isNewest) {
          errorNotification('Transaction table update has been failed');
        }
      });
  };
